import { useMemo, useRef, useEffect, useState } from 'react';
import { computeRunningValue, computeTimelineTimeState } from '@components/wallboard/helpers/batchTimeline';
import type { WallboardBatch, BatchTimelineTimeState } from '@components/wallboard/types';

export interface UseManageBatchTimeLineWithTimeParams {
  batch: WallboardBatch | null;
  offset: number;
}

export interface UseManageBatchTimeLineWithTimeReturn extends BatchTimelineTimeState {
  value: number;
  targetTimeValue: number;
  getPercent: (val: number) => number;
  scheduledTargetTotalTimeTotalInSeconds: number;
  paidTargetTotalTimeTotalInSeconds: number;
  idealTargetTotalTimeTotalInSeconds: number;
}

const useManageBatchTimeLineWithTime = ({
  batch,
  offset,
}: UseManageBatchTimeLineWithTimeParams): UseManageBatchTimeLineWithTimeReturn => {
  const mountedAtRef = useRef(Date.now());
  const [now, setNow] = useState(Date.now());

  const {
    scheduledTargetTotalTimeTotalInSeconds = 0,
    paidTargetTotalTimeTotalInSeconds = 0,
    idealTargetTotalTimeTotalInSeconds = 0,
    actualTotalTimeTotalInSeconds = 0,
    isInProgress = false,
  } = batch ?? {};

  useEffect(() => {
    if (!isInProgress) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isInProgress]);

  const offsetInSeconds = Math.floor(offset / 1000);
  const staticStartValue = actualTotalTimeTotalInSeconds ?? 0;

  const value = useMemo(() => {
    const elapsedSeconds = Math.floor((now - mountedAtRef.current) / 1000);
    return computeRunningValue({ isInProgress, staticStartValue, offsetInSeconds, elapsedSeconds });
  }, [now, staticStartValue, offsetInSeconds, isInProgress]);

  // Uses Math.max (intentionally different from productivityTimeline.ts which uses first-nonzero priority)
  const targetTimeValue = Math.max(
    scheduledTargetTotalTimeTotalInSeconds ?? 0,
    paidTargetTotalTimeTotalInSeconds ?? 0,
    idealTargetTotalTimeTotalInSeconds ?? 0
  );

  const timeState = computeTimelineTimeState(value, targetTimeValue);

  const getPercent = (val: number) => (val > 0 ? (val * 100) / targetTimeValue : 0);

  return {
    value,
    targetTimeValue,
    ...timeState,
    getPercent,
    scheduledTargetTotalTimeTotalInSeconds,
    paidTargetTotalTimeTotalInSeconds,
    idealTargetTotalTimeTotalInSeconds,
  };
};

export default useManageBatchTimeLineWithTime;
